import { watchEmail } from './registration/core/accountLookup';
import { signIn, VERDICT, REFUSED } from './registration/core/signIn';
import { showLoader, hideLoader } from './modules/loaders';

/**
 * This modal's own copy, off its own form.
 *
 * It cannot read the registration form's, because it appears on pages that draw no
 * registration form: an event that is not taking requests still offers a sign-in.
 */
let copy = null;

function say(key, fallback) {
    if (! copy) {
        const holder = document.querySelector('[data-login-alerts]');

        try {
            copy = holder ? JSON.parse(holder.dataset.loginAlerts) : {};
        } catch (error) {
            copy = {};
        }
    }

    return Object.prototype.hasOwnProperty.call(copy, key) ? copy[key] : fallback;
}

/**
 * The login modal: no account is created here, ever.
 *
 * It shares the lookup and the sign-in with the registration doors, and agrees with
 * them on nothing else. An address with no account is the failure case on this
 * screen — the opposite of what the same answer means on a door that registers —
 * and there is no profile to send along, no second form to reveal, no confirmation
 * step to hand the page to.
 *
 * The button still has two stages: the first click looks the address up and reveals
 * the password box, the second signs in. Same reason as the registration doors, a
 * password gets typed in between.
 *
 * Its markup is its own, which is why the section ids here are not core/formState's:
 * this form and a registration form can sit on one page, and the two must not toggle
 * each other's boxes.
 */

/**
 * Every one of these is drawn by _login.blade.php and by nothing else.
 *
 * #validEmailMessage and #invalidEmailMessage are deliberately absent: they belong
 * to the registration form, and the event page carries both forms at once, so
 * toggling them from here would move boxes inside the other modal. This one says
 * what it has to say in a dialog.
 */
const SECTIONS = {
    password: '#loginPasswordFields',
    code: '#loginCodeField',
    forgot: '#forgotPasswordLogin',
    forgotLink: '#forgotPasswordLinkLogin'
};

function show(names) {
    Object.keys(SECTIONS).forEach((key) => {
        $(SECTIONS[key])[names.indexOf(key) === -1 ? 'hide' : 'show']();
    });
}

function alertBox(options) {
    if (typeof Swal === 'undefined') {
        if (options.whenMissing) {
            options.whenMissing();
        }

        return;
    }

    const { whenMissing, onConfirm, ...swal } = options;
    const dialog = Swal.fire(swal);

    if (onConfirm) {
        dialog.then((result) => {
            if (result.isConfirmed) {
                onConfirm();
            }
        });
    }
}

function reload() {
    window.location.reload();
}

export function loginFlow() {
    const $form = $('#loginForm');

    if (! $form.length) {
        return;
    }

    const $button = $('#loginButton');

    let lookup = null;

    /** The label is the step, so it has to be put back when the step ends. */
    function busy(isBusy, label) {
        if (! $button.length) {
            return;
        }

        if (! $button.data('defaultLabel')) {
            $button.data('defaultLabel', $button.text().trim() || 'Login');
        }

        const text = label || $button.data('defaultLabel');

        $button.prop('disabled', Boolean(isBusy));
        $button.toggleClass('is-loading', Boolean(isBusy));

        if (isBusy) {
            $button.html('<span class="btn-spinner" aria-hidden="true"></span>' + text);
            return;
        }

        $button.text(text);
    }

    function reset() {
        show([]);
        $('#login_password').val('');
        $('#login_code_login').val('');
        busy(false);
        $button.off('click').on('click', lookUpAddress);
    }

    function lookUpAddress(event) {
        event.preventDefault();
        busy(true, 'Checking...');
        lookup.check();
    }

    function enter(event) {
        event.preventDefault();

        const code = $('#login_code_login').val();
        const password = $('#login_password').val();

        if (! code && ! password) {
            alertBox({
                title: say('error', 'Error'),
                text: say('password_or_code_required', 'Enter your password or the code we sent you.'),
                icon: 'error',
                confirmButtonText: say('close', 'Close')
            });

            return;
        }

        showLoader();
        busy(true, code ? 'Verifying...' : 'Logging in...');

        if (code) {
            auth.withCode(code);
        } else {
            auth.withPassword(password);
        }
    }

    const auth = signIn({
        emailField: '#login_email',
        profile: () => ({ simple_login: true }),
        translate: say,
        onSettled: () => {
            hideLoader();
            busy(false);
        },
        onVerdict: () => alertBox({
            title: say('login_success', 'Logged in'),
            icon: 'success',
            confirmButtonText: say('thanks', 'Thanks'),
            onConfirm: reload,
            whenMissing: reload
        }),
        onRefused: (kind, message) => {
            if (kind === REFUSED.password) {
                $('#login_password').addClass('is-invalid');
            }

            alertBox({
                title: say('error', 'Error'),
                text: kind === REFUSED.code
                    ? say('wrong_code_text', 'The login code is incorrect or expired.')
                    : message,
                icon: 'error',
                confirmButtonText: kind === REFUSED.answer
                    ? say('close', 'Close')
                    : say('try_again', 'Try Again'),
                // A refused answer is the server saying the account cannot sign in at
                // all, so the page is reloaded onto whatever state it is really in.
                onConfirm: kind === REFUSED.answer ? reload : undefined
            });
        }
    });

    lookup = watchEmail($form.get(0), {
        onExisting: () => {
            busy(false);
            show(['password', 'forgot', 'forgotLink']);
            $button.off('click').on('click', enter);
        },
        onNew: () => {
            busy(false);
            show([]);
            alertBox({
                title: say('error', 'Error'),
                text: say('email_not_registered', 'We could not find an account for that address.'),
                icon: 'error',
                confirmButtonText: say('close', 'Close')
            });
        },
        onError: () => {
            busy(false);
            alertBox({
                title: say('error', 'Error'),
                text: say('verify_email_error', 'We could not check that address. Please try again.'),
                icon: 'error',
                confirmButtonText: say('close', 'Close')
            });
        }
    });

    $('#forgotPasswordLinkLogin').on('click', (event) => {
        event.preventDefault();

        alertBox({
            title: say('sending_email', 'Sending email'),
            text: say('sending_code', 'We are sending your login code.'),
            imageUrl: '/gif/sending-email.gif',
            imageWidth: 80,
            imageHeight: 80,
            showConfirmButton: false,
            allowOutsideClick: false,
            didOpen: () => Swal.showLoading(),
            whenMissing: () => {}
        });

        auth.requestCode()
            .done(() => {
                if (typeof Swal !== 'undefined') {
                    Swal.close();
                }

                alertBox({
                    title: say('check_your_email', 'Check your email'),
                    text: say('login_code_sent', 'Login code sent. Please check your inbox.'),
                    icon: 'info',
                    confirmButtonText: say('continue', 'Continue')
                });

                show(['code']);
                $button.off('click').on('click', enter);
            })
            .fail(() => {
                if (typeof Swal !== 'undefined') {
                    Swal.close();
                }

                alertBox({
                    title: say('error', 'Error'),
                    text: say('error_sending_code', 'We could not send the login code. Please try again.'),
                    icon: 'error',
                    confirmButtonText: say('try_again', 'Try Again')
                });
            });
    });

    $('#login_email').on('input', reset);

    reset();
}

$(document).ready(loginFlow);
